export const AUDIT_SCORE_THRESHOLD = 20;

export type WarningMessageId = 'younger_than_18' | 'currently_treated' | 'pregnant' | 'suicidal_ideation' | 'high_risk';

type WarningConfig = {
  externalUrl?: string;
};

export const warningConfigs: Record<WarningMessageId, WarningConfig> = {
  younger_than_18:   { externalUrl: 'https://www.jellinek.nl/informatie-voor-jongeren/' },
  currently_treated: {},
  pregnant:          { externalUrl: 'https://www.alcoholvrijzwanger.nl/' },
  suicidal_ideation: { externalUrl: 'https://www.113.nl/' },
  high_risk:         { externalUrl: 'https://www.jellinek.nl/ik-zoek-hulp-voor-mezelf/' },
};

export type QuestionOption = {
  labelKey: string;
  value: number;
};

export type ScreeningOption = QuestionOption & {
  triggersWarning?: WarningMessageId;
};

export type AuditOption = QuestionOption;

export type ScreeningQuestion = {
  id: number;
  type: 'binary';
  i18nKey: string;
  options: ScreeningOption[];
};

export type AuditQuestion = {
  id: number;
  type: 'multichoice';
  i18nKey: string;
  hintKey?: string;
  options: AuditOption[];
};

export const screeningQuestions: ScreeningQuestion[] = [
  {
    id: 1,
    type: 'binary',
    i18nKey: 'aida_screening.questions.q1',
    options: [
      { labelKey: 'aida_screening.options.yes', value: 1 },
      { labelKey: 'aida_screening.options.no', value: 0, triggersWarning: 'younger_than_18' },
    ],
  },
  {
    id: 2,
    type: 'binary',
    i18nKey: 'aida_screening.questions.q2',
    options: [
      { labelKey: 'aida_screening.options.yes', value: 1, triggersWarning: 'currently_treated' },
      { labelKey: 'aida_screening.options.no', value: 0 },
    ],
  },
  {
    id: 3,
    type: 'binary',
    i18nKey: 'aida_screening.questions.q3',
    options: [
      { labelKey: 'aida_screening.options.yes', value: 1, triggersWarning: 'pregnant' },
      { labelKey: 'aida_screening.options.no', value: 0 },
    ],
  },
  {
    id: 4,
    type: 'binary',
    i18nKey: 'aida_screening.questions.q4',
    options: [
      { labelKey: 'aida_screening.options.yes', value: 1, triggersWarning: 'suicidal_ideation' },
      { labelKey: 'aida_screening.options.no', value: 0 },
    ],
  },
];

export const auditQuestions: AuditQuestion[] = [
  {
    id: 5,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q5',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.monthly_or_less', value: 1 },
      { labelKey: 'aida_screening.options.two_to_four_per_month', value: 2 },
      { labelKey: 'aida_screening.options.two_to_three_per_week', value: 3 },
      { labelKey: 'aida_screening.options.four_or_more_per_week', value: 4 },
    ],
  },
  {
    id: 6,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q6',
    hintKey: 'aida_screening.hints.standard_drinks',
    options: [
      { labelKey: 'aida_screening.options.none', value: 0 },
      { labelKey: 'aida_screening.options.one_or_two', value: 1 },
      { labelKey: 'aida_screening.options.three_or_four', value: 2 },
      { labelKey: 'aida_screening.options.five_or_six', value: 3 },
      { labelKey: 'aida_screening.options.seven_to_nine', value: 4 },
      { labelKey: 'aida_screening.options.ten_or_more', value: 5 },
    ],
  },
  {
    id: 7,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q7',
    hintKey: 'aida_screening.hints.standard_drinks',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
  {
    id: 8,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q8',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
  {
    id: 9,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q9',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
  {
    id: 10,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q10',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
  {
    id: 11,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q11',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
  {
    id: 12,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q12',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
  {
    id: 13,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q13',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
  {
    id: 14,
    type: 'multichoice',
    i18nKey: 'aida_screening.questions.q14',
    options: [
      { labelKey: 'aida_screening.options.never', value: 0 },
      { labelKey: 'aida_screening.options.less_than_once_per_month', value: 1 },
      { labelKey: 'aida_screening.options.monthly', value: 2 },
      { labelKey: 'aida_screening.options.weekly', value: 3 },
      { labelKey: 'aida_screening.options.almost_daily', value: 4 },
    ],
  },
];

export function computeAuditScore(answers: Record<number, number>): number {
  const auditQuestionIds = auditQuestions.map((q) => q.id);
  return Object.entries(answers)
    .filter(([id]) => auditQuestionIds.includes(Number(id)))
    .reduce((sum, [, val]) => sum + val, 0);
}

export function isEligibleForResearch(answers: Record<number, number>): boolean {
  const totalAuditValue = computeAuditScore(answers);
  const warningTriggered = screeningQuestions.some((q) => {
    const matchingAnswer = answers[q.id];
    return q.options.some((o) => o.value === matchingAnswer && o.triggersWarning);
  }) || totalAuditValue >= AUDIT_SCORE_THRESHOLD;
  return !warningTriggered;
}
